<template>
  <div>
    <div class="mb-4">
      <v-btn color="grey" variant="outlined"to="/setting/staff"> {{ $t('settings.backToList') }} </v-btn>
    </div>

    <v-row v-if="staffDetail" no-gutters>
      <page-title
        :value="staffDetail.lastName + staffDetail.firstName"
      ></page-title>
      <v-spacer />
      <v-btn @click="editStaff" color="primary" variant="flat"> {{ $t('settings.staffEdit') }} </v-btn>
    </v-row>

    <v-row v-else>
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </v-row>

    <v-container class="pt-12" v-if="staffDetail">
      <v-row>
        <v-col cols="4"> {{ $t('settings.staffCategory') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.category == "general" ? $t('settings.general') : $t('settings.help') }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('common.name') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.lastName + staffDetail.firstName }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.position') }} </v-col>
        <v-col cols="8">
          {{
            (!!staffDetail.isChief ? $t('settings.chief') : "") +
            " " +
            (!!staffDetail.isDirector ? $t('settings.director') : "") +
            " " +
            (!!staffDetail.isAdmin ? $t('settings.administrator') : "")
          }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.staffCode1') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.staffCode1 }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.staffCode2') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.staffCode2 }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.birthDate') }} </v-col>
        <v-col cols="8">
          {{ formatDisplayDate(staffDetail.birth) }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('common.gender') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.gender == "male" ? $t('common.male') : $t('common.female') }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.phoneNumber1') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.phoneNumber1 }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.phoneNumber2') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.phoneNumber2 }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.joinDate') }} </v-col>
        <v-col cols="8">
          {{ formatDisplayDate(staffDetail.joinDate) }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.assignmentDate') }} </v-col>
        <v-col cols="8">
          {{ formatDisplayDate(staffDetail.assignDate) }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.retireDate') }} </v-col>
        <v-col cols="8">
          {{ formatDisplayDate(staffDetail.retireDate) }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.retireReason') }} </v-col>
        <v-col cols="8">
          {{ getRetireReasonText(staffDetail.retireReason || "") }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.retireReasonDetail') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.retireReasonComment }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4"> {{ $t('settings.remarks') }} </v-col>
        <v-col cols="8">
          {{ staffDetail.comment }}
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import PageTitle from "@/components/atoms/PageTitle.vue";
import LocalDataService from "@/service/LocalDataService";
import { staffModule } from "@/store/dataModules/staffModule";
import { staffEditModule } from "@/store/dataModules/staffEditModule";
import Swal from "sweetalert2";

export default defineComponent({
  name: "StaffDetail",

  components: {
    PageTitle,
  },

  mounted() {
    this.getStaff();
  },

  data() {
    return {
      retireSelectValues: [
        { text: "", value: "" },
        { text: this.$t('settings.transfer'), value: "transfer" },
        { text: this.$t('settings.retirement'), value: "retire" },
        { text: this.$t('settings.death'), value: "die" },
      ],
    };
  },

  computed: {
    userNurseryId: function () {
      return this.$route.params.id;
    },
    staffDetail: function () {
      return staffModule.staffDetail;
    },
  },

  methods: {
    async getStaff() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await staffModule.getStaff({
          nurseryId: String(nurseryId),
          userNurseryId: this.userNurseryId as string,
        });
      }
    },
    async deleteStaff() {
      Swal.fire({
        title: this.$t('settings.warning'),
        text: this.$t('settings.deleteStaffConfirm'),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "red",
        cancelButtonColor: "grey",
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          const nurseryId = LocalDataService.getNurseryId();
          if (nurseryId) {
            staffEditModule
              .deleteStaff({
                nurseryId: String(nurseryId),
                userNurseryId: this.userNurseryId as string,
              })
              .then((response) => {
                if (response) {
                  Swal.fire({ title: this.$t('settings.deletedSuccessfully'), icon: "success" });
                  this.$router.push({ path: "/setting/staff" });
                } else {
                  Swal.fire({ title: this.$t('common.failed'), icon: "error" });
                }
              });
          }
        }
      });
    },
    editStaff() {
      this.$router.push({ path: `/setting/staff/${this.userNurseryId}/edit` });
    },
    getRetireReasonText(value: string): string {
      return this.retireSelectValues.find((r) => r.value == value)?.text || "";
    },
    formatDisplayDate(value?: string): string {
      return value ? dayjs(value).format("DD-MM-YYYY") : "";
    },
  },
});
</script>
